/**
 * Team planner: who works on which project when. Rows are people, columns
 * are days; projects are dragged from the side list (or picked in the
 * dialog) onto a person's day. Capacity comes from each person's weekly
 * plan; absences and company holidays are shown in the cells.
 */
import { useMemo, useState, type ChangeEvent, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InputText as Input } from "@primereact/ui/inputtext";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { PageHeader } from "../../components/ui/PageHeader.js";
import {
  DateInput,
  useMediaQuery,
  FilterTabs,
  Modal,
  SearchBox,
  SelectInput,
  toDateValue,
} from "../../components/ui/controls.js";
import { cx } from "../../components/ui/cx.js";
import { apiDeleteBody, apiGet, apiPatch, apiPost } from "../../lib/api.js";
import { ErrorPanel, LoadingPanel, MutationError } from "../shared.js";
import { PlannerDayList } from "./PlannerDayList.js";
import {
  type PlannerMode,
  type PlannerAssignment,
  type PlannerData,
  type PlannerConflict,
  type EditorState,
  startOfLocalDay,
  plannerDays,
  moveAnchor,
  minutesBetween,
  hours,
  parseConflicts,
  loadClass,
} from "./planner-model.js";

export function TeamPlanner() {
  const { t, i18n } = useTranslation();
  const client = useQueryClient();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const [mode, setMode] = useState<PlannerMode>("week");
  const [anchor, setAnchor] = useState(() => startOfLocalDay(new Date()));
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [conflicts, setConflicts] = useState<PlannerConflict[]>([]);
  const [teamId, setTeamId] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [projectFilterId, setProjectFilterId] = useState("");
  const [projectSearch, setProjectSearch] = useState("");
  const [showProjects, setShowProjects] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const filtered = Boolean(teamId || employeeId || projectFilterId);
  const days = useMemo(() => plannerDays(anchor, mode), [anchor, mode]);
  const from = toDateValue(days[0]!);
  const to = toDateValue(days.at(-1)!);
  const today = toDateValue(new Date());
  const compact = mode === "month";
  const wide = useMediaQuery("(min-width: 768px)");

  const planner = useQuery({
    queryKey: ["planner", from, to, teamId, employeeId, projectFilterId],
    queryFn: () =>
      apiGet<{ data: PlannerData }>("/planner", {
        query: {
          from,
          to,
          teamId: teamId || undefined,
          userId: employeeId || undefined,
          projectId: projectFilterId || undefined,
        },
      }).then((response) => response.data),
    placeholderData: (previous) => previous,
  });
  const data = planner.data;

  const byCell = useMemo(() => {
    const assignments = new Map<string, PlannerAssignment[]>();
    for (const assignment of data?.assignments ?? []) {
      const key = `${assignment.userId}:${assignment.localDate}`;
      assignments.set(key, [...(assignments.get(key) ?? []), assignment]);
    }
    const absences = new Map<string, PlannerData["absences"]>();
    for (const absence of data?.absences ?? []) {
      const key = `${absence.userId}:${absence.date}`;
      absences.set(key, [...(absences.get(key) ?? []), absence]);
    }
    return { assignments, absences };
  }, [data?.assignments, data?.absences]);
  const holidays = new Map(
    (data?.holidays ?? []).map((holiday) => [holiday.date, holiday.name]),
  );
  const projects = (data?.backlog ?? []).filter((project) =>
    `${project.number} ${project.title}`
      .toLocaleLowerCase()
      .includes(projectSearch.trim().toLocaleLowerCase()),
  );
  const projectOptions = (data?.backlog ?? []).map((project) => ({
    value: project.id,
    label: `${project.number} · ${project.title}`,
  }));
  const memberOptions = (data?.resources ?? []).map((resource) => ({
    value: resource.id,
    label: `${resource.firstName} ${resource.lastName}`,
  }));

  const openCreate = (projectId = "", userId = "", localDate = from) => {
    setConflicts([]);
    setEditor({
      assignmentId: null,
      version: null,
      projectId,
      userId,
      localDate,
      startTime: "08:00",
      endTime: "12:00",
      notes: "",
    });
  };
  const openEdit = (assignment: PlannerAssignment) => {
    setConflicts([]);
    setEditor({
      assignmentId: assignment.id,
      version: assignment.version,
      projectId: assignment.projectId,
      userId: assignment.userId,
      localDate: assignment.localDate,
      startTime: assignment.startTime,
      endTime: assignment.endTime,
      notes: assignment.notes ?? "",
    });
  };
  const close = () => {
    setEditor(null);
    setConflicts([]);
  };
  const refresh = async () => {
    close();
    await client.invalidateQueries({ queryKey: ["planner"] });
  };
  const save = useMutation({
    mutationFn: (force: boolean) => {
      if (!editor) throw new Error("Planner editor is closed");
      const body = {
        projectId: editor.projectId,
        userId: editor.userId,
        localDate: editor.localDate,
        startTime: editor.startTime,
        endTime: editor.endTime,
        ...(editor.notes ? { notes: editor.notes } : {}),
        force,
      };
      return editor.assignmentId
        ? apiPatch(`/planner/assignments/${editor.assignmentId}`, {
            ...body,
            version: editor.version,
          })
        : apiPost("/planner/assignments", body);
    },
    onSuccess: refresh,
    onError: (error) => setConflicts(parseConflicts(error)),
  });
  const remove = useMutation({
    mutationFn: () =>
      apiDeleteBody(`/planner/assignments/${editor!.assignmentId}`, {
        version: editor!.version,
      }),
    onSuccess: refresh,
  });
  const setField = (key: keyof EditorState, value: string) =>
    setEditor((current) => (current ? { ...current, [key]: value } : null));
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setConflicts([]);
    save.mutate(false);
  };

  const dateFormat = (options: Intl.DateTimeFormatOptions, date: Date) =>
    new Intl.DateTimeFormat(locale, options).format(date);
  const rangeLabel =
    mode === "day"
      ? dateFormat({ dateStyle: "full" }, days[0]!)
      : mode === "month"
        ? dateFormat({ month: "long", year: "numeric" }, days[0]!)
        : `${dateFormat({ dateStyle: "medium" }, days[0]!)} – ${dateFormat({ dateStyle: "medium" }, days.at(-1)!)}`;

  const projectList = (
    <div className="flex h-full flex-col">
      <SearchBox
        value={projectSearch}
        onChange={setProjectSearch}
        placeholder={t("planner.searchProjects", {
          defaultValue: "Search projects",
        })}
      />
      <p className="mt-2 text-xs text-app-muted">
        {t("planner.dragHint", {
          defaultValue: "Drag a project onto a day, or click it to plan it.",
        })}
      </p>
      <ul className="mt-3 space-y-1.5 overflow-y-auto">
        {projects.map((project) => (
          <li key={project.id}>
            <button
              type="button"
              draggable
              onDragStart={(event) =>
                event.dataTransfer.setData("text/project-id", project.id)
              }
              onClick={() => openCreate(project.id)}
              className={cx(
                "block w-full rounded-lg border px-2.5 py-2 text-left text-sm transition-colors hover:border-brand",
                project.status === "on_hold"
                  ? "border-warning-border bg-warning-soft"
                  : "border-app-border bg-app-surface",
              )}
            >
              <span className="block text-xs font-semibold text-app-muted">
                {project.number}
                {project.status === "on_hold"
                  ? ` · ${t("status.on_hold")}`
                  : ""}
              </span>
              <span className="block truncate">{project.title}</span>
            </button>
          </li>
        ))}
        {!projects.length ? (
          <li className="text-sm text-app-muted">
            {t("planner.noBacklog", {
              defaultValue: "No open projects.",
            })}
          </li>
        ) : null}
      </ul>
    </div>
  );

  return (
    <>
      <PageHeader
        title={t("planner.title", { defaultValue: "Planner" })}
        subtitle={t("planner.subtitle", {
          defaultValue: "Plan who works on which project and when.",
        })}
        actions={
          <Button type="button" icon="pi pi-plus" onClick={() => openCreate()}>
            {t("planner.newAssignment", { defaultValue: "New assignment" })}
          </Button>
        }
      />

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outlined"
            severity="secondary"
            icon="pi pi-chevron-left"
            aria-label={t("planner.previous", { defaultValue: "Previous" })}
            onClick={() => setAnchor(moveAnchor(anchor, mode, -1))}
          />
          <Button
            type="button"
            variant="outlined"
            severity="secondary"
            onClick={() => setAnchor(startOfLocalDay(new Date()))}
          >
            {t("planner.today", { defaultValue: "Today" })}
          </Button>
          <Button
            type="button"
            variant="outlined"
            severity="secondary"
            icon="pi pi-chevron-right"
            aria-label={t("planner.next", { defaultValue: "Next" })}
            onClick={() => setAnchor(moveAnchor(anchor, mode, 1))}
          />
          <DateInput
            className="w-40"
            ariaLabel={t("planner.goTo", { defaultValue: "Go to date" })}
            value={toDateValue(anchor)}
            onChange={(value) => {
              if (value) setAnchor(startOfLocalDay(new Date(`${value}T12:00`)));
            }}
          />
          <span className="text-sm font-medium text-app-text">
            {rangeLabel}
          </span>
        </div>
        <FilterTabs
          value={mode}
          onChange={setMode}
          options={(["day", "week", "month"] as const).map((value) => ({
            value,
            label: t(`planner.${value}`, { defaultValue: value }),
          }))}
        />
      </div>

      <div className="mb-3 sm:hidden">
        <Button
          type="button"
          size="small"
          variant="outlined"
          severity={filtered ? undefined : "secondary"}
          icon="pi pi-filter"
          aria-expanded={showFilters}
          onClick={() => setShowFilters((value) => !value)}
        >
          {t("planner.filters")}
        </Button>
      </div>
      <div
        className={cx(
          "mb-4 grid-cols-1 gap-3 sm:grid sm:grid-cols-3",
          showFilters ? "grid" : "hidden",
        )}
      >
        <SelectInput
          ariaLabel={t("planner.team", { defaultValue: "Team" })}
          value={teamId}
          onChange={(value) => {
            setTeamId(value);
            setEmployeeId("");
          }}
          placeholder={t("planner.allTeams", { defaultValue: "All teams" })}
          options={[
            {
              value: "",
              label: t("planner.allTeams", { defaultValue: "All teams" }),
            },
            ...(data?.teams ?? []).map((team) => ({
              value: team.id,
              label: team.name,
            })),
          ]}
        />
        <SelectInput
          ariaLabel={t("planner.teamMember", { defaultValue: "Team member" })}
          value={employeeId}
          onChange={setEmployeeId}
          placeholder={t("planner.allPeople", { defaultValue: "Everyone" })}
          options={[
            {
              value: "",
              label: t("planner.allPeople", { defaultValue: "Everyone" }),
            },
            ...memberOptions,
          ]}
        />
        <SelectInput
          ariaLabel={t("projects.project", { defaultValue: "Project" })}
          value={projectFilterId}
          onChange={setProjectFilterId}
          placeholder={t("planner.allProjects", {
            defaultValue: "All projects",
          })}
          filter
          options={[
            {
              value: "",
              label: t("planner.allProjects", {
                defaultValue: "All projects",
              }),
            },
            ...projectOptions,
          ]}
        />
      </div>

      {planner.isLoading ? (
        <LoadingPanel />
      ) : planner.error ? (
        <ErrorPanel error={planner.error} />
      ) : !data?.resources.length ? (
        <p className="rounded-xl border border-dashed border-app-border p-6 text-center text-sm text-app-muted">
          {t("planner.noTeam", { defaultValue: "No active team members." })}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[15rem_minmax(0,1fr)]">
          <aside className="hidden rounded-xl border border-app-border bg-app-surface p-3 xl:block xl:max-h-[calc(100vh-14rem)]">
            <h2 className="mb-2 text-sm font-semibold">
              {t("planner.projects", { defaultValue: "Projects" })}
            </h2>
            {projectList}
          </aside>
          <div className="xl:hidden">
            <Button
              type="button"
              size="small"
              variant="outlined"
              severity="secondary"
              icon="pi pi-briefcase"
              onClick={() => setShowProjects((value) => !value)}
            >
              {t("planner.projects", { defaultValue: "Projects" })}
            </Button>
            {showProjects ? (
              <div className="mt-2 max-h-80 rounded-xl border border-app-border bg-app-surface p-3">
                {projectList}
              </div>
            ) : null}
          </div>

          {!wide ? (
            <PlannerDayList
              days={days}
              data={data}
              byCell={byCell}
              holidays={holidays}
              locale={locale}
              onCreate={openCreate}
              onEdit={openEdit}
            />
          ) : (
            <div className="overflow-auto rounded-xl border border-app-border bg-app-surface">
              <table className="w-full border-separate border-spacing-0 text-sm">
                <thead>
                  <tr>
                    <th className="sticky left-0 top-0 z-20 min-w-28 border-b sm:min-w-40 border-r border-app-border bg-app-background px-3 py-2 text-left font-medium">
                      {t("planner.teamMember", { defaultValue: "Team member" })}
                    </th>
                    {days.map((day) => {
                      const date = toDateValue(day);
                      const holiday = holidays.get(date);
                      const weekend = day.getDay() === 0 || day.getDay() === 6;
                      return (
                        <th
                          key={date}
                          title={holiday}
                          className={cx(
                            "sticky top-0 z-10 border-b border-r border-app-border px-2 py-2 text-left font-medium",
                            compact ? "min-w-16" : "min-w-28 sm:min-w-36",
                            date === today
                              ? "bg-brand-soft text-brand"
                              : holiday || weekend
                                ? "bg-app-background text-app-muted"
                                : "bg-app-background",
                          )}
                        >
                          <span className="block text-xs">
                            {dateFormat({ weekday: "short" }, day)}
                          </span>
                          <span className="block">
                            {dateFormat(
                              compact
                                ? { day: "numeric" }
                                : { day: "numeric", month: "short" },
                              day,
                            )}
                          </span>
                          {holiday && !compact ? (
                            <span className="block truncate text-xs font-normal text-warning">
                              {holiday}
                            </span>
                          ) : null}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {data.resources.map((resource) => (
                    <tr key={resource.id}>
                      <th
                        scope="row"
                        className="sticky left-0 z-10 border-b border-r border-app-border bg-app-surface px-3 py-2 text-left align-top font-medium"
                      >
                        {resource.firstName} {resource.lastName}
                      </th>
                      {days.map((day) => {
                        const date = toDateValue(day);
                        const key = `${resource.id}:${date}`;
                        const assignments = byCell.assignments.get(key) ?? [];
                        const absences = byCell.absences.get(key) ?? [];
                        const weekday = day.getDay() || 7;
                        const capacity = holidays.has(date)
                          ? 0
                          : (resource.workSchedules.find(
                              (schedule) => schedule.weekday === weekday,
                            )?.plannedMinutes ?? 0);
                        const booked =
                          assignments.reduce(
                            (sum, item) =>
                              sum + minutesBetween(item.startAt, item.endAt),
                            0,
                          ) +
                          absences.reduce((sum, item) => sum + item.minutes, 0);
                        return (
                          <td
                            key={date}
                            className={cx(
                              "border-b border-r border-app-border align-top",
                              compact ? "h-16 p-1" : "h-24 p-1.5",
                              capacity === 0 && "bg-app-background/60",
                            )}
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={(event) => {
                              const projectId =
                                event.dataTransfer.getData("text/project-id");
                              const assignmentId =
                                event.dataTransfer.getData(
                                  "text/assignment-id",
                                );
                              if (projectId)
                                openCreate(projectId, resource.id, date);
                              const moved = data.assignments.find(
                                (item) => item.id === assignmentId,
                              );
                              if (moved)
                                openEdit({
                                  ...moved,
                                  userId: resource.id,
                                  localDate: date,
                                });
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => openCreate("", resource.id, date)}
                              aria-label={t("planner.assignOn", {
                                defaultValue: "Plan {{name}} on {{date}}",
                                name: `${resource.firstName} ${resource.lastName}`,
                                date,
                              })}
                              className="group mb-1 block w-full text-left"
                            >
                              <span className="flex items-center justify-between text-[11px] text-app-muted group-hover:text-brand">
                                <span>
                                  {hours(booked)}
                                  {compact ? "" : ` / ${hours(capacity)}`}
                                </span>
                                <i className="pi pi-plus text-[10px] opacity-0 group-hover:opacity-100" />
                              </span>
                              <span className="mt-0.5 block h-1 overflow-hidden rounded bg-app-background">
                                <span
                                  className={cx(
                                    "block h-full",
                                    loadClass(booked, capacity),
                                  )}
                                  style={{
                                    width: `${Math.min(100, capacity ? (booked / capacity) * 100 : booked ? 100 : 0)}%`,
                                  }}
                                />
                              </span>
                            </button>
                            {absences.map((absence, index) => (
                              <span
                                key={`${absence.kind}-${index}`}
                                className="mb-1 block truncate rounded bg-warning-soft px-1.5 py-0.5 text-[11px] text-warning"
                              >
                                {t(`time.kind.${absence.kind}`)}
                              </span>
                            ))}
                            {assignments.map((assignment) => (
                              <button
                                key={assignment.id}
                                type="button"
                                draggable
                                onDragStart={(event) =>
                                  event.dataTransfer.setData(
                                    "text/assignment-id",
                                    assignment.id,
                                  )
                                }
                                onClick={() => openEdit(assignment)}
                                title={`${assignment.project.number} · ${assignment.project.title} · ${assignment.startTime}–${assignment.endTime}`}
                                className="mb-1 block w-full rounded-md border border-info-border bg-info-soft px-1.5 py-1 text-left text-xs text-info hover:shadow-sm"
                              >
                                {compact ? (
                                  <span className="block truncate font-semibold">
                                    {assignment.project.number
                                      .split("-")
                                      .at(-1)}
                                  </span>
                                ) : (
                                  <>
                                    <span className="block truncate font-semibold">
                                      {assignment.project.title}
                                    </span>
                                    <span className="block opacity-80">
                                      {assignment.startTime}–
                                      {assignment.endTime}
                                    </span>
                                  </>
                                )}
                              </button>
                            ))}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <Modal
        open={editor !== null}
        onClose={close}
        title={
          editor?.assignmentId
            ? t("planner.editAssignment", { defaultValue: "Edit assignment" })
            : t("planner.newAssignment", { defaultValue: "New assignment" })
        }
      >
        {editor ? (
          <form id="planner-assignment" className="space-y-4" onSubmit={submit}>
            <MutationError
              error={conflicts.length ? null : save.error || remove.error}
            />
            {conflicts.length ? (
              <div className="rounded-lg border border-warning-border bg-warning-soft p-3 text-sm text-warning">
                <strong>
                  {t("planner.conflicts", {
                    defaultValue: "Scheduling conflicts",
                  })}
                </strong>
                <ul className="mt-2 list-disc pl-5">
                  {conflicts.map((conflict, index) => {
                    const projectId = data?.assignments.find(
                      (item) => item.id === conflict.assignmentId,
                    )?.projectId;
                    return (
                      <li key={`${conflict.type}-${index}`}>
                        {conflict.type === "holiday"
                          ? `${t("planner.conflict.holiday")}: ${conflict.message}`
                          : t(`planner.conflict.${conflict.type}`, {
                              defaultValue: conflict.message,
                            })}
                        {projectId ? (
                          <>
                            {" · "}
                            <Link
                              className="underline"
                              to={`/projects/${projectId}`}
                            >
                              {t("planner.openConflict", {
                                defaultValue: "Open project",
                              })}
                            </Link>
                          </>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
            <Field
              label={t("projects.project", { defaultValue: "Project" })}
              required
            >
              <SelectInput
                value={editor.projectId}
                onChange={(value) => setField("projectId", value)}
                options={projectOptions}
                filter
                placeholder={t("planner.selectProject", {
                  defaultValue: "Select project",
                })}
              />
            </Field>
            <Field
              label={t("planner.teamMember", { defaultValue: "Team member" })}
              required
            >
              <SelectInput
                value={editor.userId}
                onChange={(value) => setField("userId", value)}
                options={memberOptions}
                placeholder={t("planner.selectMember", {
                  defaultValue: "Select team member",
                })}
              />
            </Field>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Field
                label={t("common.date", { defaultValue: "Date" })}
                required
              >
                <DateInput
                  value={editor.localDate}
                  onChange={(value) => setField("localDate", value)}
                />
              </Field>
              <Field
                label={t("planner.startTime", { defaultValue: "Start" })}
                required
              >
                <Input
                  type="time"
                  fluid
                  value={editor.startTime}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setField("startTime", event.target.value)
                  }
                />
              </Field>
              <Field
                label={t("planner.endTime", { defaultValue: "End" })}
                required
              >
                <Input
                  type="time"
                  fluid
                  value={editor.endTime}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setField("endTime", event.target.value)
                  }
                />
              </Field>
            </div>
            <Field label={t("common.notes", { defaultValue: "Notes" })}>
              <Input
                fluid
                value={editor.notes}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setField("notes", event.target.value)
                }
              />
            </Field>
            <div className="flex flex-wrap justify-between gap-2 pt-2">
              <div>
                {editor.assignmentId ? (
                  <Button
                    type="button"
                    severity="danger"
                    variant="outlined"
                    loading={remove.isPending}
                    onClick={() => remove.mutate()}
                  >
                    {t("common.delete")}
                  </Button>
                ) : null}
              </div>
              <div className="flex gap-2">
                {conflicts.length ? (
                  <Button
                    type="button"
                    severity="warn"
                    loading={save.isPending}
                    onClick={() => save.mutate(true)}
                  >
                    {t("planner.force", { defaultValue: "Schedule anyway" })}
                  </Button>
                ) : null}
                <Button
                  type="submit"
                  disabled={
                    !editor.projectId ||
                    !editor.userId ||
                    editor.endTime <= editor.startTime
                  }
                  loading={save.isPending}
                >
                  {t("common.save")}
                </Button>
              </div>
            </div>
          </form>
        ) : null}
      </Modal>
    </>
  );
}
